import { useCallback, useEffect, useState } from 'react';

const isBrowser = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

export const useLocalStorage = <T,>(key: string | null, initialValue: T) => {
  const [storedValue, setStoredValue] = useState<T>(initialValue);
  const [ready, setReady] = useState<boolean>(() => !isBrowser());

  useEffect(() => {
    if (!key) {
      setStoredValue(initialValue);
      setReady(true);
      return;
    }

    if (!isBrowser()) {
      setStoredValue(initialValue);
      setReady(true);
      return;
    }

    try {
      const item = window.localStorage.getItem(key);
      if (item) {
        setStoredValue(JSON.parse(item) as T);
      } else {
        window.localStorage.setItem(key, JSON.stringify(initialValue));
        setStoredValue(initialValue);
      }
    } catch (error) {
      console.error('Impossible de lire localStorage', error);
      setStoredValue(initialValue);
    }
    setReady(true);
  }, [initialValue, key]);

  const setValue = useCallback(
    (value: T | ((current: T) => T)) => {
      setStoredValue((prev) => {
        const nextValue = value instanceof Function ? value(prev) : value;
        if (key && isBrowser()) {
          try {
            window.localStorage.setItem(key, JSON.stringify(nextValue));
          } catch (error) {
            console.error("Impossible d'écrire dans localStorage", error);
          }
        }
        return nextValue;
      });
    },
    [key]
  );

  return [storedValue, setValue, ready] as const;
};

